import React from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { performLogout } from "../../../api/UserAPIUtils";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import { useNavigate } from "react-router-dom";

interface DropdownMenuProps {
  setActiveComponent: (component: string) => void;
  email: string;
  fullname: string;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  setActiveComponent,
  email,
  fullname,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const role = useAppSelector((state) => state.user.role?.toLocaleLowerCase());

  const handleMenuButtonClick = (activeComponent: string) => {
    setActiveComponent(activeComponent);
  };

  const handleChangePasswordButtonClick = () => {
    navigate("/change-password");
  };

  const handleLogout = () => {
    dispatch(performLogout());
    navigate("/login");
  };

  const initials = fullname
    ? fullname
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((name) => name[0]?.toUpperCase())
        .join("")
    : "U";

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <MenuButton className="inline-flex items-center gap-3 rounded-2xl bg-transparent p-0 transition hover:bg-slate-50 md:bg-white md:px-3 md:py-2 md:shadow-sm md:ring-1 md:ring-slate-200">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-sm font-semibold text-white shadow-[0_10px_24px_rgba(0,128,128,0.2)]">
            {initials}
          </div>
          <div className="hidden min-w-0 md:block">
            <p className="truncate text-sm font-semibold text-slate-800">
              {fullname || "User"}
            </p>
            <p className="truncate text-xs text-slate-500">
              {email || "No email provided"}
            </p>
          </div>
        </MenuButton>
      </div>

      <MenuItems
        transition
        className="absolute right-0 z-10 mt-3 w-64 origin-top-right rounded-[24px] bg-white p-2 shadow-[0_24px_70px_rgba(15,23,42,0.14)] ring-1 ring-slate-200 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
      >
        <div className="rounded-[18px] bg-slate-50 px-4 py-4">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
            Signed in as
          </p>
          <p className="mt-2 truncate text-sm font-semibold text-slate-900">
            {fullname || "User"}
          </p>
          <p className="mt-1 truncate text-sm text-slate-500">
            {email || "No email provided"}
          </p>
        </div>

        <div className="mt-2 space-y-1">
          {role !== "" && (
            <>
              <MenuItem>
                <button
                  onClick={() => handleMenuButtonClick("profile")}
                  className="block w-full rounded-2xl px-4 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 hover:text-slate-900 focus:outline-none"
                >
                  Profile
                </button>
              </MenuItem>
              {role !== "student" && (
                <MenuItem>
                  <button
                    onClick={() => handleChangePasswordButtonClick()}
                    className="block w-full rounded-2xl px-4 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 hover:text-slate-900 focus:outline-none"
                  >
                    Change Password
                  </button>
                </MenuItem>
              )}
            </>
          )}
        </div>

        <div className="mt-2 border-t border-slate-100 pt-2">
          <MenuItem>
            <button
              onClick={handleLogout}
              className="block w-full rounded-2xl px-4 py-3 text-left text-sm font-medium text-red-500 transition hover:bg-red-50 focus:outline-none"
            >
              Logout
            </button>
          </MenuItem>
        </div>
      </MenuItems>
    </Menu>
  );
};

export default DropdownMenu;
